import { InputRule, Node, PasteRule } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";

/**
 * Tiptap extension that preserves Liquid expressions ({{ ... }} and {% ... %})
 * through the editor's parse/serialize round-trip without HTML escaping, while
 * still letting users edit the source inside the pill.
 *
 * Without this, `{% if x < 5 %}` would lose its `<` to entity encoding the
 * first time it round-trips through `editor.getHTML()`.
 *
 * Strategy:
 *   1. Before Tiptap parses incoming HTML, wrap each Liquid expression in a
 *      <liquid-placeholder> element. The Liquid source becomes the element's
 *      text content, with `< > &` HTML-encoded so the parser survives.
 *   2. The LiquidNode parses those placeholders as inline nodes whose
 *      `content: "text*"` keeps the source editable.
 *   3. On output, getHTML() emits the placeholder elements; we strip them
 *      back out and decode HTML entities to recover the raw source.
 */

const PLACEHOLDER_TAG = "liquid-placeholder";

const UNWRAP_REGEX = new RegExp(
  `<${PLACEHOLDER_TAG}[^>]*>([\\s\\S]*?)</${PLACEHOLDER_TAG}>`,
  "g",
);

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function decodeHtmlEntities(str) {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

/**
 * Walk the raw HTML string and replace Liquid expressions found in text
 * content (not inside attribute values) with placeholder elements. Done at
 * the string level so Liquid containing `<` or `>` survives — DOMParser
 * would have mangled it before we got a chance to capture it.
 */
export function wrapLiquidPlaceholders(html) {
  if (!html) return html;

  let result = "";
  let i = 0;
  let inTag = false;
  // Track quoted attribute values so a `>` inside `<a title="x > y">` doesn't
  // get treated as the tag close — null when not in a quoted value, otherwise
  // the opening quote character.
  let quote = null;

  while (i < html.length) {
    const ch = html[i];
    const next = html[i + 1] || "";

    if (!inTag && ch === "<" && /[a-zA-Z/!?]/.test(next)) {
      inTag = true;
      result += ch;
      i++;
      continue;
    }

    if (inTag && quote === null && (ch === '"' || ch === "'")) {
      quote = ch;
      result += ch;
      i++;
      continue;
    }

    if (inTag && ch === quote) {
      quote = null;
      result += ch;
      i++;
      continue;
    }

    if (inTag && quote === null && ch === ">") {
      inTag = false;
      result += ch;
      i++;
      continue;
    }

    if (!inTag && ch === "{" && (next === "{" || next === "%")) {
      const closeMarker = next === "{" ? "}}" : "%}";
      const closeIdx = html.indexOf(closeMarker, i + 2);
      if (closeIdx !== -1) {
        const source = html.slice(i, closeIdx + 2);
        result += `<${PLACEHOLDER_TAG}>${escapeHtml(source)}</${PLACEHOLDER_TAG}>`;
        i = closeIdx + 2;
        continue;
      }
    }

    result += ch;
    i++;
  }

  return result;
}

export function unwrapLiquidPlaceholders(html) {
  if (!html) return html;
  return html.replace(UNWRAP_REGEX, (_match, inner) =>
    decodeHtmlEntities(inner),
  );
}

// Standard Liquid block tags. Open/close pairs wrap content; branches mark
// alternate cases inside an open/close (and must NOT be split out of the
// surrounding <p>, since lifting around them breaks HTML).
const BLOCK_OPEN = "if|unless|for|case|tablerow|capture";
const BLOCK_CLOSE = "endif|endunless|endfor|endcase|endtablerow|endcapture";
const BLOCK_BRANCH = "else|elsif|when";

// `[^%]*?` keeps the body contained to a single Liquid tag — `[\s\S]*?` would
// allow the regex to backtrack across `%}` boundaries and swallow neighbouring
// tags, e.g. matching `{% if … %}…{% endif %}` as a single "tag".
const liquidTagFragment = (tags) => `\\{%-?\\s*(?:${tags})\\b[^%]*?-?%\\}`;

// `<p\b[^>]*>` tolerates attributes Tiptap might add later (class, style, …);
// matching only the bare `<p>` would silently stop lifting and conditionals
// would render as empty paragraphs again.
const STANDALONE_TAG_RE = new RegExp(
  `<p\\b[^>]*>\\s*(${liquidTagFragment([BLOCK_OPEN, BLOCK_CLOSE, BLOCK_BRANCH].join("|"))})\\s*</p>`,
  "g",
);

const SINGLE_PARAGRAPH_BLOCK_RE = new RegExp(
  `<p\\b[^>]*>\\s*(${liquidTagFragment(BLOCK_OPEN)})\\s*` +
    `(<br[^>]*>[\\s\\S]*?<br[^>]*>)` +
    `\\s*(${liquidTagFragment(BLOCK_CLOSE)})\\s*</p>`,
  "g",
);

const HAS_BRANCH_RE = new RegExp(`\\{%-?\\s*(?:${BLOCK_BRANCH})\\b`);

/**
 * Lift Liquid control-flow tags out of the <p> wrappers Tiptap puts around
 * them. Without this, an `{% if x %}` block whose body is a single paragraph
 * serialises as `<p>{% if x %}<br>…<br>{% endif %}</p>` and renders as an
 * empty `<p></p>` in the email when the condition is false.
 *
 * Two cases handled:
 *   1. Single-paragraph block (body separated from tags by <br>):
 *      <p>{% if x %}<br>body<br>{% endif %}</p>
 *      → {% if x %}<p><br>body<br></p>{% endif %}
 *   2. Multi-paragraph block (each tag in its own <p>):
 *      <p>{% if x %}</p><p>body</p><p>{% endif %}</p>
 *      → {% if x %}<p>body</p>{% endif %}
 *
 * Conditionals containing {% else %}/{% elsif %}/{% when %} in the same <p>
 * are left alone — lifting around them would split the wrapper across
 * branches and produce broken HTML.
 */
export function liftLiquidBlockTags(html) {
  if (!html) return html;
  const lifted = html.replace(
    SINGLE_PARAGRAPH_BLOCK_RE,
    (match, open, body, close) =>
      HAS_BRANCH_RE.test(body) ? match : `${open}<p>${body}</p>${close}`,
  );
  return lifted.replace(STANDALONE_TAG_RE, "$1");
}

export const LiquidNode = Node.create({
  name: "liquid",
  group: "inline",
  inline: true,
  content: "text*",
  selectable: true,

  // Higher than CustomComponent (50) so we win the wildcard tag race.
  priority: 1000,

  parseHTML() {
    return [{ tag: PLACEHOLDER_TAG }];
  },

  renderHTML({ HTMLAttributes }) {
    return [PLACEHOLDER_TAG, { class: "tiptap-liquid", ...HTMLAttributes }, 0];
  },

  // ProseMirror won't split an inline node, so Enter inside a Liquid pill
  // does nothing by default. Move the cursor out past the pill first, then
  // delegate to the standard splitBlock.
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { state } = this.editor;
        const { $from, empty } = state.selection;
        if (!empty || $from.parent.type !== this.type) return false;

        const liquidStart = $from.before();
        const liquidNode = state.doc.nodeAt(liquidStart);
        if (!liquidNode) return false;
        const afterLiquid = liquidStart + liquidNode.nodeSize;

        return this.editor
          .chain()
          .setTextSelection(afterLiquid)
          .splitBlock()
          .run();
      },
    };
  },

  addInputRules() {
    const buildLiquidNode = (state, range, source) => {
      const node = this.type.create(null, state.schema.text(source));
      state.tr.replaceWith(range.from, range.to, node);
    };

    // Avoid nesting pills if a rule fires while the cursor is already inside
    // one (e.g. user editing the source of an existing pill).
    const isInsideLiquid = (state, pos) => {
      const $pos = state.doc.resolve(pos);
      for (let d = $pos.depth; d > 0; d--) {
        if ($pos.node(d).type.name === "liquid") return true;
      }
      return false;
    };

    return [
      new InputRule({
        find: /(\{\{[^{}]*\}\})$/,
        handler: ({ state, range, match }) => {
          if (isInsideLiquid(state, range.from)) return;
          buildLiquidNode(state, range, match[1]);
        },
      }),
      new InputRule({
        find: /(\{%[^{}]*%\})$/,
        handler: ({ state, range, match }) => {
          if (isInsideLiquid(state, range.from)) return;
          buildLiquidNode(state, range, match[1]);
        },
      }),
      // Auto-close `{%` to `{%  %}` and land the cursor between the spaces, so
      // the pill exists immediately and the user types the body inside it.
      new InputRule({
        find: /\{%$/,
        handler: ({ state, range }) => {
          if (isInsideLiquid(state, range.from)) return;
          const node = this.type.create(null, state.schema.text("{%  %}"));
          state.tr.replaceWith(range.from, range.to, node);
          // +1 to enter the node, +3 to land between the two spaces.
          state.tr.setSelection(
            TextSelection.create(state.tr.doc, range.from + 4),
          );
        },
      }),
      // Auto-close `{{` to `{{}}` (no spaces) so the Suggestion plugin's
      // query tracking sees user input directly after the trigger — a space
      // ends the query and dismisses the dropdown. The variable dropdown
      // still works; picking from it replaces the pill with a spaced form.
      new InputRule({
        find: /\{\{$/,
        handler: ({ state, range }) => {
          if (isInsideLiquid(state, range.from)) return;
          const node = this.type.create(null, state.schema.text("{{}}"));
          state.tr.replaceWith(range.from, range.to, node);
          // +1 to enter the node, +2 to land between `{{` and `}}`.
          state.tr.setSelection(
            TextSelection.create(state.tr.doc, range.from + 3),
          );
        },
      }),
    ];
  },

  // Catches Liquid in plain-text pastes (clipboards with no text/html payload).
  // HTML pastes are handled upstream by `transformPastedHTML` on the editor.
  addPasteRules() {
    const buildLiquidNode = (state, range, source) => {
      const node = this.type.create(null, state.schema.text(source));
      state.tr.replaceWith(range.from, range.to, node);
    };

    return [
      new PasteRule({
        find: /(\{\{[^{}]*\}\})/g,
        handler: ({ state, range, match }) =>
          buildLiquidNode(state, range, match[1]),
      }),
      new PasteRule({
        find: /(\{%[^{}]*%\})/g,
        handler: ({ state, range, match }) =>
          buildLiquidNode(state, range, match[1]),
      }),
    ];
  },
});
